/**
 * Rich-text helpers for term meanings.
 *
 * Meanings are authored in the admin Quill editor and stored as HTML in the
 * existing `meaning_*` text fields (no backend change). Before rendering that
 * HTML on the public term page we run it through `sanitizeRichHtml`, an
 * allowlist sanitizer that keeps only the tags/attributes the editor produces
 * and strips anything dangerous (scripts, event handlers, javascript: URLs…).
 *
 * Older entries are plain text with no markup — `htmlLooksRich` lets the UI
 * fall back to the legacy plain-text renderer for those.
 */

/** Tags the Quill toolbar can emit and that we allow through to the DOM. */
const ALLOWED_TAGS = new Set([
  "P",
  "BR",
  "DIV",
  "SPAN",
  "H1",
  "H2",
  "H3",
  "BLOCKQUOTE",
  "UL",
  "OL",
  "LI",
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "S",
  "STRIKE",
  "DEL",
  "A",
  "PRE",
  "CODE",
]);

/** Elements removed outright (content discarded), never unwrapped. */
const DANGEROUS_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "LINK",
  "META",
  "BASE",
  "FORM",
  "INPUT",
  "BUTTON",
  "TEXTAREA",
  "SVG",
  "MATH",
]);

/** Inline-style properties allowed on any element (color/highlight/alignment). */
const ALLOWED_STYLE_PROPS = new Set([
  "color",
  "background-color",
  "text-align",
]);

function sanitizeStyleAttr(value: string): string {
  const safe: string[] = [];
  for (const decl of value.split(";")) {
    const idx = decl.indexOf(":");
    if (idx === -1) {
      continue;
    }
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const val = decl.slice(idx + 1).trim();
    if (!ALLOWED_STYLE_PROPS.has(prop) || !val) {
      continue;
    }
    // Block anything that could load a resource or execute.
    if (/url\s*\(|expression|javascript:/i.test(val)) {
      continue;
    }
    safe.push(`${prop}: ${val}`);
  }
  return safe.join("; ");
}

/** Allow only http(s)/mailto and same-app relative (`/`, `#`) links. */
function sanitizeHref(href: string): string | null {
  const v = href.trim();
  if (/^(https?:|mailto:)/i.test(v)) {
    return v;
  }
  if (v.startsWith("/") || v.startsWith("#")) {
    return v;
  }
  return null;
}

function cleanElementAttributes(el: Element): void {
  const tag = el.tagName.toUpperCase();
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();

    if (name === "href" && tag === "A") {
      const safe = sanitizeHref(attr.value);
      if (safe) {
        el.setAttribute("href", safe);
      } else {
        el.removeAttribute("href");
      }
      continue;
    }

    if (name === "class") {
      // Keep only Quill's own utility classes (alignment, indent…).
      const tokens = attr.value
        .split(/\s+/)
        .filter(t => /^ql-[\w-]+$/.test(t));
      if (tokens.length) {
        el.setAttribute("class", tokens.join(" "));
      } else {
        el.removeAttribute("class");
      }
      continue;
    }

    if (name === "style") {
      const safe = sanitizeStyleAttr(attr.value);
      if (safe) {
        el.setAttribute("style", safe);
      } else {
        el.removeAttribute("style");
      }
      continue;
    }

    // Drop everything else: on* handlers, src, id, data-*, etc.
    el.removeAttribute(attr.name);
  }

  if (tag === "A") {
    el.setAttribute("target", "_blank");
    el.setAttribute("rel", "noopener noreferrer nofollow");
  }
}

function cleanNode(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tag = el.tagName.toUpperCase();

      if (DANGEROUS_TAGS.has(tag)) {
        el.remove();
        continue;
      }

      // Depth-first: sanitize the subtree before deciding on `el` itself, so
      // unwrapping a disallowed wrapper promotes already-clean children.
      cleanNode(el);

      if (ALLOWED_TAGS.has(tag)) {
        cleanElementAttributes(el);
      } else {
        while (el.firstChild) {
          node.insertBefore(el.firstChild, el);
        }
        el.remove();
      }
    } else if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
    }
  }
}

/** Strip everything outside the allowlist; returns HTML safe for `dangerouslySetInnerHTML`. */
export function sanitizeRichHtml(html: string): string {
  if (!html || typeof document === "undefined") {
    return "";
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  cleanNode(doc.body);
  return doc.body.innerHTML;
}

/** True when the string carries inline markup (i.e. came from the rich editor). */
export function htmlLooksRich(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return /<(p|br|div|span|h[1-3]|ul|ol|li|blockquote|b|strong|i|em|u|s|strike|del|a|pre|code)\b[^>]*>/i.test(
    value
  );
}

/** Strip markup to readable plain text (for previews, "key insight", etc.). */
export function richTextToPlainText(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  if (!htmlLooksRich(value)) {
    return value;
  }
  if (typeof document !== "undefined") {
    const doc = new DOMParser().parseFromString(value, "text/html");
    // Keep paragraph/list breaks as spaces so sentences don't run together.
    return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
  }
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** True when rich-editor output has no visible text/content (e.g. Quill's `<p><br></p>`). */
export function richTextIsEmpty(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  const withoutTags = value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/ /g, " ")
    .trim();
  return withoutTags.length === 0;
}
